import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.css";
import "../style/HomePage.css";
import { FaDownload, FaSearch } from "react-icons/fa";
import ModalArticles from "./ModalArticles";
import Papa from "papaparse"
import { Alert } from "bootstrap";
import ManualForm from "./ManualForm";
import { SCHEMALINK_URL } from "./schemalink";
import { type } from "@testing-library/user-event/dist/type";

// Maps the KG display names Schema Link sends (?kg=...) to this app's
// internal view keys (same values the KG radio buttons below set via
// setGraphName), so schemalink triples flow through the pipeline exactly
// like a KG chosen by hand would.
const SCHEMALINK_KG_TO_GRAPHDB = {
    'miRNA-KG': 'mirnadiseasegoview',
    'PKT-KG': 'diseasemech',
    'Hetionet': 'hetionet',
    'PrimeKG': 'primekg',
    'OptimusKG': 'optimuskg',
};

function HomePage() {

    const navigate = useNavigate();
    const location = useLocation();
    const runButtonRef = useRef(null);
    const [inputValue, setInputValue] = useState("");
    const [abstractData, setAbstractData] = useState([]);
    const [newData, setNewData] = useState({});
    const [relationMethod, setRelationMehtod] = useState('search');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [manualFile, setManualFile] = useState();
    const [graphName, setGraphName] = useState('mirnadiseasegoview');
    const [visMethod, setVisMethod] = useState('clm');
    const [embMethod, setEmbMethod] = useState('transe')
    const [noSchemalinkData, setNoSchemalinkData] = useState(false);
    const pendingPollStarted = useRef(false);

    const handleClick = async () => {
        const query = inputValue.split(" ").join(" AND ");
        try {
            const api_query = `https://bio-viber.biodata.di.unimi.it/api/pubmed_gathering?query="${query}"&max=10`;
            const response = await fetch(api_query);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setAbstractData(data);
            return data;
        } catch (error) {
            const data = "Error";
            console.log(error.message);
            return data;
        }
    };

    const prova = (data) => {
        if (data === "Error") {
            console.log("ERROR");
        }

        setNewData(data);
    };

    const handleRun = () => {
        console.log(inputValue === true);
        if (relationMethod === 'search' && inputValue) {
            setIsModalOpen(true);
            // Send triples data to DisplayGraph
            // navigate("/displayGraph", {
            //     state: newData,
            // }); 
        }
        else if (relationMethod === 'csv' && selectedFile) {
            setIsModalOpen(true);
        }
        else if (relationMethod === 'manual' && manualFile) {
            setIsModalOpen(true);
        }
        else if (relationMethod === 'schemalink') {
            setIsModalOpen(true);
        }
    };


    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) {
            alert("Please enter a non-empty csv file.");
            return;
        }
        // TODO add new state for file before parsing
        setSelectedFile(file);
    }

    const handleDownload = () => {
        const csv_file = ['subject','predicate','object','subject_TYPE','object_TYPE','PubMedID'];
        const blob = new Blob([csv_file], {type:"text/csv"});
        const element = document.createElement("a");
        element.href = URL.createObjectURL(blob);
        element.download = "sample_file" + ".csv";
        document.body.appendChild(element);         
        element.click();
    }

    useEffect(() => console.log(manualFile), [manualFile]);

    // Schema Link (SK) integration: SK opens this page with ?json=<triples>&kg=<selectedKG>
    // in the URL. On mount, forward whatever came in to the backend (which stores it
    // server-side, keyed by kg) and then drive the same relMethod='schemalink' pipeline
    // the manual/csv flows use: raw-triples Modal -> grounding -> graph.
    useEffect(() => {
        const incomingParams = new URLSearchParams(location.search);
        const incomingJson = incomingParams.get('json');
        const incomingKg = incomingParams.get('kg');

        if (!incomingJson || !incomingKg) return;

        const graphDBKey = SCHEMALINK_KG_TO_GRAPHDB[incomingKg];
        if (!graphDBKey) {
            console.log(`>>> schemalink: unrecognized kg "${incomingKg}"`);
            return;
        }

        const forwardSchemalinkData = async () => {
            try {
                const apiUrl = new URL('https://bio-viber.biodata.di.unimi.it/api/schemalink');
                apiUrl.searchParams.set('json', incomingJson);
                apiUrl.searchParams.set('kg', incomingKg);

                const response = await fetch(apiUrl.toString());
                const data = await response.json();
                console.log('>>> schemalink data received by API:', data);

                // Triples are now stored server-side (keyed by this kg) -- switch
                // into the schemalink pipeline the same way picking a KG radio
                // button + manual/csv relMethod would.
                setGraphName(graphDBKey);
                setRelationMehtod('schemalink');
            } catch (error) {
                console.log('>>> schemalink forward failed:', error.message);
            }
        };

        forwardSchemalinkData();
    }, [location.search]);

    // SK actually delivers triples via its own background fetch straight to
    // /api/schemalink -- separate from whatever opens this tab -- so the tab's
    // own URL never carries ?json=&kg= for the effect above to find. Instead,
    // poll the backend right after mount to ask "is there SK data waiting".
    // Consume-once on the backend side, so this only fires for the delivery
    // that (presumably) caused this page load, not for a later unrelated visit.
    useEffect(() => {
        // StrictMode runs this effect twice; a second poller would find the
        // flag already consumed and wrongly report "no data"
        if (pendingPollStarted.current) return;
        pendingPollStarted.current = true;
        let attempts = 0;
        const maxAttempts = 16; // ~8s total, generous for the fetch + this page's own load to land in either order

        // Returns true once SK data has been picked up and the pipeline started
        const pollForPendingData = async () => {
            while (attempts < maxAttempts) {
                attempts += 1;
                try {
                    const response = await fetch('https://bio-viber.biodata.di.unimi.it/api/schemalink/pending');
                    const data = await response.json();
                    if (data.pending) {
                        console.log('>>> schemalink: pending data found', data);
                        const graphDBKey = SCHEMALINK_KG_TO_GRAPHDB[data.kg];
                        if (!graphDBKey) {
                            console.log(`>>> schemalink: unrecognized kg "${data.kg}"`);
                            return false;
                        }
                        setGraphName(graphDBKey);
                        setRelationMehtod('schemalink');
                        return true;
                    }
                } catch (error) {
                    console.log('>>> schemalink pending check failed:', error.message);
                }
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
            return false;
        };

        // With no SK data, show a message whose Close goes back to Schema Link
        pollForPendingData().then((started) => {
            if (!started) {
                setNoSchemalinkData(true);
            }
        });
    }, []);

    // Once relationMethod flips to 'schemalink' (and the Run button below has
    // re-rendered with data-bs-toggle/data-bs-target pointing at #modalGetTriple),
    // click it programmatically -- this reuses the exact same Bootstrap-modal-open
    // + handleRun() path a manual/csv user click would take, instead of duplicating it.
    useEffect(() => {
        if (relationMethod === 'schemalink') {
            runButtonRef.current?.click();
        }
    }, [relationMethod]);

    // The homepage form is never shown (see main-container below) -- make the
    // page and Bootstrap's backdrop behind the modal plain white instead of
    // grey/dimmed (see .schemalink-mode in HomePage.css). The class is on
    // <body> because that's where Bootstrap appends the backdrop; removed
    // again when leaving the page.
    useEffect(() => {
        document.body.classList.add('schemalink-mode');
        return () => document.body.classList.remove('schemalink-mode');
    }, []);

    return (
        <>
            <ModalArticles
                data={abstractData}
                input={inputValue}
                dataUpdate={prova}
                relMethod={relationMethod}
                file={selectedFile}
                manualFile={manualFile}
                isModalOpen={isModalOpen}
                graphName={graphName}
                visMethod={visMethod}
                embMethod={embMethod}
            />
            <ManualForm manualFile={setManualFile}/>

            {noSchemalinkData && (
                <div className="container text-center mt-5">
                    <p className="text-danger">No Schema Link data received.</p>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => { window.location.href = SCHEMALINK_URL; }}
                    >
                        Close
                    </button>
                </div>
            )}

            {/* Bio-Viber is only used from Schema Link: the modal opens on its own and
                is the only thing the user needs, so the homepage form is never shown.
                visibility (not display) so the Run button stays in the layout for the
                programmatic click above. Without SK data a message with a Close button
                is shown instead (see the polling effect). */}
            <div className="main-container" style={{ visibility: 'hidden' }}>
                <div className="container">
                    <div className="row title-row ">
                        <div className="col content-container" style={{backgroundColor: 'none'}}>
                            <span className="title">Bio-Viber</span>
                            <p className="titleUnder">
                                <b>
                                    Virtual Intelligent Biomedical Relation
                                    Extraction Assistant
                                </b>
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">

                        <div className="col view">
                            <div className="header">
                                <h3>Knowledge Graph</h3>
                            </div>

                            <div className="graph input-group ml-module" >

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option1"
                                        id="flexRadioDefault2"
                                        onClick={() => setGraphName('mirnadiseasegoview')}
                                        defaultChecked
                                    />
                                    <label
                                        className="form-check-label"
                                        for="flexRadioDefault2"
                                    >
                                        miRNA-KG
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option1"
                                        id="flexRadioDefault5"
                                        onClick={() => setGraphName('diseasemech')}
                                    />
                                    <label
                                        className="form-check-label"
                                        for="flexRadioDefault5"
                                    >
                                        PKT-KG
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option1"
                                        id="flexRadioDefault1"
                                        onClick={() => setGraphName('hetionet')}
                                    />
                                    <label
                                        className="form-check-label"
                                        for="flexRadioDefault1"
                                    >
                                        Hetionet
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option1"
                                        id="flexRadioDefault3"
                                        onClick={() => setGraphName('primekg')}
                                    />
                                    <label
                                        className="form-check-label"
                                        for="flexRadioDefault3"
                                    >
                                        PrimeKG
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option1"
                                        id="flexRadioDefault4"
                                        onClick={() => setGraphName('optimuskg')}
                                    />
                                    <label
                                        className="form-check-label"
                                        for="flexRadioDefault4"
                                    >
                                        OptimusKG
                                    </label>
                                </div>



                            </div>
                        </div>

                        <div className="col repo-col">
                            <div className="header">
                                <h3>Relation Repository</h3>
                            </div>

                            <div className="input-group mb-3">
                                <div className="input-group-text">
                                    <input
                                        className="form-check-input mt-0"
                                        name="option2"
                                        type="radio"
                                        value=""
                                        aria-label="Radio button for following text input"
                                        onClick={() => setRelationMehtod('search')}
                                        defaultChecked
                                    />
                                </div>
                                <input
                                    id="inputGPT"
                                    type="text"
                                    className="form-control"
                                    aria-describedby="button-addon2"
                                    placeholder="️Google PubMed and select an article 📄"
                                    value={inputValue}
                                    onChange={(e) => {
                                        setInputValue(e.target.value);
                                    }}
                                    disabled={relationMethod !== 'search'}
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    id="button-addon2"
                                    data-bs-toggle="modal"
                                    data-bs-target="#modalArticles"
                                    disabled={!inputValue.trim() || relationMethod !== 'search'}
                                    onClick={handleClick}
                                >
                                    <span>
                                        <FaSearch />
                                    </span>
                                </button>
                            </div>

                            <div className="input-group mb-3">
                                <div className="input-group-text">
                                    <input
                                        className="form-check-input mt-0"
                                        name="option2"
                                        type="radio"
                                        value=""
                                        aria-label="Radio button for following text input"
                                        onClick={() => setRelationMehtod('csv')}
                                    />
                                </div>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="inputGroupFile02"
                                    onChange={handleFile}
                                    disabled={relationMethod !== 'csv'}
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    title="Download sample csv file"
                                    onClick={handleDownload}
                                    disabled={relationMethod !== 'csv'}
                                >
                                    <span>
                                        <FaDownload />
                                    </span>
                                </button>
                            </div>

                            <div className="input-group form manual-form">
                                <div className="input-group-text ">
                                    <input
                                        className="form-check-input mt-0"
                                        name="option2"
                                        type="radio"
                                        value=""
                                        aria-label="Radio button"
                                        onClick={() => setRelationMehtod('manual')}
                                    />
                                </div>
                                <button
                                    className="btn btn-secondary manual-btn"
                                    type="button"
                                    data-bs-toggle="modal"
                                    data-bs-target="#formModal"
                                    disabled={relationMethod !== 'manual'}
                                >
                                    Fill form manually 📝
                                </button>
                            </div>
                        </div>

                    </div>

                    <div className="row align-items-center">
                        
                        <div className="col ml short">
                            <div className="header">
                                <h3>Plausibility Approach</h3>
                            </div>

                            <div className="input-group ml-module">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option3"
                                        id="MLModule1"
                                        onClick={() => setEmbMethod('transe')}
                                        defaultChecked
                                    />
                                    <label
                                        className="form-check-label"
                                        for="MLModule1"
                                    >
                                        TransE + Random Forest
                                    </label>
                                </div>

                                {/* <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option3"
                                        id="MLModule2"
                                        onClick={() => setEmbMethod('n2v')}
                                    />
                                    <label
                                        className="form-check-label"
                                        for="MLModule2"
                                    >
                                        Node2Vec + One Class SVM
                                    </label>
                                </div> */}
                            </div>

                        </div>

                        <div className="col short">
                            <div className="header extract">
                                <h3>Extraction Method</h3>
                            </div>

                            <div className="input-group ml-module">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option4"
                                        id="extraction_method"
                                        checked
                                    />
                                    <label
                                        className="form-check-label"
                                        for="extraction_method"
                                    >
                                        SPIRES
                                    </label>
                                </div>
                            </div>

                        </div>

                        <div className="col short">
                            <div className="header">
                                <h3>Visualization Algorithm</h3>
                            </div>

                            <div className="input-group ml-module">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option5"
                                        id="RadioVis3"
                                        onClick={() => setVisMethod('clm')}
                                        defaultChecked
                                    />
                                    <label
                                        className="form-check-label"
                                        for="RadioVis3"
                                    >
                                        Context Link (Multiple Contexts)
                                    </label>
                                </div>
                            </div>

                            <div className="input-group ml-module">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option5"
                                        id="RadioVis2"
                                        onClick={() => setVisMethod('cls')}
                                    />
                                    <label
                                        className="form-check-label"
                                        for="RadioVis2"
                                    >
                                        Context Link (Single Context)
                                    </label>
                                </div>
                            </div>

                            <div className="input-group ml-module">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="option5"
                                        id="RadioVis1"
                                        onClick={() => setVisMethod('steiner')}
                                    />
                                    <label
                                        className="form-check-label"
                                        for="RadioVis1"
                                    >
                                        Steiner
                                    </label>
                                </div>
                            </div>

                        </div>
                        {/* graph name, vis method here */}


                    </div>
                    <div className="row btn-row">
                        <div className="col col-22">
                            <button
                                ref={runButtonRef}
                                className="btn btn-primary"
                                type="button"
                                data-bs-toggle={relationMethod === 'manual' && manualFile || relationMethod === 'csv' && selectedFile || relationMethod === 'schemalink' ? "modal" : ""}
                                data-bs-target={relationMethod === 'search' && inputValue ? "#modalAfterChoice": relationMethod === 'csv' && selectedFile || relationMethod === 'manual' && manualFile || relationMethod === 'schemalink' ? "#modalGetTriple" : ""}
                                onClick={handleRun}
                                >
                                Run
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default HomePage;
